import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Plus, Trash2, Target, Zap, Sparkles, Lock } from "lucide-react";
import ActionControls, { useUndoRedo } from "@/components/ActionControls";
import AIVerticalMeasure from "@/components/AIVerticalMeasure";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const VerticalTracker = () => {
  const navigate = useNavigate();
  const [chartData, setChartData] = useState([]);
  const [stats, setStats] = useState({ initial: 0, current: 0, improvement: 0 });
  const [newJump, setNewJump] = useState({ height_inches: '', notes: '' });
  const [loading, setLoading] = useState(true);
  const [aiMeasureOpen, setAiMeasureOpen] = useState(false);
  const [hasAIAccess, setHasAIAccess] = useState(false);
  const [userTier, setUserTier] = useState("free");
  
  // Use undo/redo hook for jumps
  const { 
    state: jumps, 
    setState: setJumps, 
    undo, 
    redo, 
    reset, 
    canUndo, 
    canRedo,
    history 
  } = useUndoRedo([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchJumpData();
    fetchFeatureAccess();
  }, []);

  const fetchFeatureAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/subscription/feature-access`, { headers });
      setHasAIAccess(!!res.data.has_advanced_ai);
      setUserTier(res.data.tier || "free");
    } catch (e) {
      // Fail closed — assume free tier on error
      setHasAIAccess(false);
    }
  };

  const handleAIMeasureClick = () => {
    if (!hasAIAccess) {
      toast.info("AI Vertical Measure is a Pro/Elite feature. Redirecting…");
      setTimeout(() => navigate("/subscription"), 800);
      return;
    }
    setAiMeasureOpen(true);
  };

  const fetchJumpData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [jumpsRes, progressRes] = await Promise.all([
        axios.get(`${API}/jumps`, { headers }),
        axios.get(`${API}/jumps/progress`, { headers })
      ]);

      setJumps(jumpsRes.data);
      
      const progressData = progressRes.data;
      if (progressData.progress) {
        const chart = progressData.progress.map(p => ({
          date: new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          inches: p.height_inches
        }));
        setChartData(chart);
      }
      
      setStats({
        initial: progressData.initial || 0,
        current: progressData.current || 0,
        improvement: progressData.improvement || 0
      });
    } catch (error) {
      console.error('Error fetching jump data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddJump = async (e) => {
    e.preventDefault();
    
    if (!newJump.height_inches || newJump.height_inches <= 0) {
      toast.error('Please enter a valid height');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.post(`${API}/jumps`, {
        height_inches: parseFloat(newJump.height_inches),
        notes: newJump.notes || null
      }, { headers });
      
      toast.success('Jump logged successfully!');
      setNewJump({ height_inches: '', notes: '' });
      fetchJumpData();
    } catch (error) {
      toast.error('Failed to log jump');
    }
  };

  const handleDeleteJump = async (jumpId) => {
    if (!window.confirm('Delete this jump entry?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/jumps/${jumpId}`, { headers });
      toast.success('Jump deleted');
      fetchJumpData();
    } catch (error) {
      toast.error('Failed to delete jump');
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all vertical jump data? This cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/jumps/all`, { headers });
      reset();
      setChartData([]);
      setStats({ initial: 0, current: 0, improvement: 0 });
      toast.success('All jump data reset!');
    } catch (error) {
      toast.error('Failed to reset data');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-[#FF4500]/30 border-t-[#FF4500] rounded-full animate-spin mx-auto mb-4" />
          <div className="text-[#FF4500] text-lg font-heading uppercase tracking-wide">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-6 lg:p-8 relative">
      <div className="noise-overlay" />
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-6xl mx-auto space-y-8"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4500] mb-2">Track Your Inches</p>
            <h1 className="text-5xl md:text-7xl font-heading text-white tracking-tight uppercase leading-none">
              VERTICAL
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <ActionControls
              onUndo={undo}
              onRedo={redo}
              onReset={handleReset}
              canUndo={canUndo}
              canRedo={canRedo}
              variant="compact"
              resetLabel="Reset all jump data"
            />
            <span className="text-[9px] font-mono text-white/30">History: {history.length}</span>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div variants={itemVariants} className="grid md:grid-cols-3 gap-px bg-white/[0.06]" data-testid="vertical-stats">
          {[
            { label: "Starting Vertical", value: stats.initial > 0 ? `${stats.initial}"` : "-", testId: "initial-vertical", icon: Target },
            { label: "Current Vertical", value: stats.current > 0 ? `${stats.current}"` : "-", testId: "current-vertical", icon: TrendingUp },
            { label: "Improvement", value: stats.improvement > 0 ? `+${stats.improvement.toFixed(1)}"` : "0", testId: "improvement", icon: Zap, highlight: true }
          ].map((stat, idx) => (
            <motion.div key={idx} whileHover={{ y: -2 }}>
              <div className="bg-[#0A0A0A] p-5 halftone">
                <stat.icon className={`w-5 h-5 ${stat.highlight ? 'text-[#FF4500]' : 'text-white/40'} mb-3`} />
                <p className="text-[9px] font-mono uppercase tracking-[0.2em] text-white/30 mb-1">{stat.label}</p>
                <p className={`text-4xl font-heading ${stat.highlight ? 'text-[#FF4500] text-glow' : 'text-white'}`} data-testid={stat.testId}>
                  {stat.value}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Chart and Log Form */}
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Progress Chart */}
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <Card className="bg-zinc-900/80 border border-zinc-800" data-testid="progress-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading font-bold text-white uppercase tracking-tight flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-orange-500" />
                  Progress Over Time
                </CardTitle>
              </CardHeader>
              <CardContent>
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorVertical" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="date" 
                        stroke="#52525b" 
                        style={{ fontSize: '10px' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis 
                        stroke="#52525b" 
                        style={{ fontSize: '10px' }}
                        axisLine={false}
                        tickLine={false}
                        label={{ value: 'Inches', angle: -90, position: 'insideLeft', style: { fill: '#52525b', fontSize: '10px' } }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#18181b',
                          border: '1px solid #27272a',
                          borderRadius: '0',
                          color: '#fff',
                          fontSize: '12px'
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="inches" 
                        stroke="#f97316" 
                        strokeWidth={2}
                        fill="url(#colorVertical)"
                        dot={{ fill: '#f97316', r: 4, strokeWidth: 0 }}
                        activeDot={{ r: 6, fill: '#f97316', stroke: '#fff', strokeWidth: 2 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[280px] flex flex-col items-center justify-center text-zinc-600">
                    <TrendingUp className="w-12 h-12 mb-3 opacity-30" />
                    <p className="text-sm">No data yet</p>
                    <p className="text-xs text-zinc-700">Log your first vertical jump!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Log New Jump */}
          <motion.div variants={itemVariants}>
            <Card className="bg-zinc-900/80 border border-zinc-800" data-testid="log-jump-form">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading font-bold text-white uppercase tracking-tight flex items-center gap-2">
                  <Plus className="w-5 h-5 text-emerald-500" />
                  Log Jump
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddJump} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="height" className="text-xs uppercase tracking-wide text-zinc-400">Height (inches)</Label>
                    <Input
                      id="height"
                      data-testid="jump-height-input"
                      type="number"
                      step="0.5"
                      placeholder="28.5"
                      value={newJump.height_inches}
                      onChange={(e) => setNewJump({ ...newJump, height_inches: e.target.value })}
                      required
                      className="bg-zinc-950 border-zinc-700 focus:border-orange-500 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes" className="text-xs uppercase tracking-wide text-zinc-400">Notes (optional)</Label>
                    <Input
                      id="notes"
                      data-testid="jump-notes-input"
                      type="text"
                      placeholder="Max effort, felt strong"
                      value={newJump.notes}
                      onChange={(e) => setNewJump({ ...newJump, notes: e.target.value })}
                      className="bg-zinc-950 border-zinc-700 focus:border-orange-500 text-white"
                    />
                  </div>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      data-testid="submit-jump-button"
                      type="submit"
                      className="w-full bg-orange-600 hover:bg-orange-500 text-white font-black uppercase tracking-wide btn-tactical"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Log Jump
                    </Button>
                  </motion.div>
                </form>

                {/* AI Measure Divider */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-zinc-800" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-zinc-900 px-3 text-xs uppercase tracking-widest text-zinc-500">or</span>
                  </div>
                </div>

                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    type="button"
                    onClick={handleAIMeasureClick}
                    className={
                      hasAIAccess
                        ? "w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-black uppercase tracking-wide btn-tactical"
                        : "w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-black uppercase tracking-wide btn-tactical border border-zinc-700 relative"
                    }
                    data-testid="ai-measure-vertical-btn"
                  >
                    {hasAIAccess ? (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Measure with AI Camera
                      </>
                    ) : (
                      <>
                        <Lock className="w-4 h-4 mr-2 text-amber-500" />
                        <span>AI Camera Measure</span>
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] bg-amber-500/20 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded">
                          PRO
                        </span>
                      </>
                    )}
                  </Button>
                </motion.div>
                <p className="text-[10px] text-zinc-600 text-center mt-2 uppercase tracking-widest">
                  {hasAIAccess
                    ? "Beta • Phone camera + AI"
                    : `Upgrade to Pro or Elite to unlock${userTier !== "free" ? "" : ""}`}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Jump History */}
        <motion.div variants={itemVariants}>
          <Card className="bg-zinc-900/80 border border-zinc-800" data-testid="jump-history">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading font-bold text-white uppercase tracking-tight">
                Jump History ({jumps.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AnimatePresence>
                {jumps.length > 0 ? (
                  <div className="space-y-2">
                    {jumps.map((jump, idx) => (
                      <motion.div 
                        key={jump.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ delay: idx * 0.05 }}
                        className="flex justify-between items-center p-3 bg-zinc-950/50 border border-zinc-800 hover:border-zinc-700 transition-colors group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-500/10 border border-orange-500/30 flex items-center justify-center font-heading font-bold text-orange-500">
                            {jump.height_inches}"
                          </div>
                          <div>
                            <p className="text-sm font-bold text-white">
                              {new Date(jump.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </p>
                            {jump.notes && (
                              <p className="text-xs text-zinc-500">{jump.notes}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteJump(jump.id)}
                          className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-500 hover:bg-red-500/10 transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-zinc-600">
                    <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No jumps logged yet</p>
                    <p className="text-xs text-zinc-700">Start tracking your vertical progress!</p>
                  </div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>

      <AIVerticalMeasure
        open={aiMeasureOpen}
        onOpenChange={setAiMeasureOpen}
        onSaved={fetchJumpData}
      />
    </div>
  );
};

export default VerticalTracker;
